/**
 * Small inline icons for the landing page.
 *
 * They are page decoration, not shared UI, so they live beside the landing rather than in the design
 * system. Each draws with `currentColor`, so the accent is set once in CSS, and each is marked
 * `aria-hidden` because the adjacent heading already names it.
 */

/** Props common to the landing icons. */
interface IconProps {
  /** Pixel size of the square icon. Defaults to 28. */
  readonly size?: number;
}

/**
 * A map-pin mark — the Greasemap motif — with a hole punched in the surface colour.
 *
 * @param props - Component props.
 * @param props.size - Pixel size of the square icon.
 * @param props.title - When set, the mark is exposed to assistive tech with this label; otherwise
 * it is decorative and hidden.
 * @returns The pin SVG.
 */
export function PinMark({ size = 28, title }: IconProps & { readonly title?: string }): React.JSX.Element {
  const labelled = title !== undefined && title !== '';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      focusable="false"
      role={labelled ? 'img' : 'presentation'}
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? title : undefined}
    >
      {labelled ? <title>{title}</title> : null}
      <path
        fill="currentColor"
        d="M12 2c-4.14 0-7.5 3.36-7.5 7.5 0 5.25 6.4 11.6 6.95 12.13a.8.8 0 0 0 1.1 0C13.1 21.1 19.5 14.75 19.5 9.5 19.5 5.36 16.14 2 12 2Z"
      />
      <circle cx="12" cy="9.5" r="2.75" fill="var(--gm-color-surface)" />
    </svg>
  );
}

/**
 * A wrench, for the garage pillar.
 *
 * @param props - Component props.
 * @param props.size - Pixel size of the square icon.
 * @returns The wrench SVG.
 */
export function WrenchIcon({ size = 28 }: IconProps): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" focusable="false" aria-hidden={true}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.7 6.3a3.5 3.5 0 0 0 4.6 4.6l-2.1-2.1 1.4-1.4 2.1 2.1a3.5 3.5 0 0 1-4.9 4.9l-7 7a2.1 2.1 0 0 1-3-3l7-7a3.5 3.5 0 0 1 1.4-5.6Z"
      />
    </svg>
  );
}

/**
 * A chequered finish flag, for the racing pillar.
 *
 * @param props - Component props.
 * @param props.size - Pixel size of the square icon.
 * @returns The flag SVG.
 */
export function FlagIcon({ size = 28 }: IconProps): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" focusable="false" aria-hidden={true}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 3v18M5 4h12l-2 4 2 4H5"
      />
    </svg>
  );
}
